import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Paper, IconButton, Popover } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createPost } from '../../services/postService';
import { getCharactersByUserId } from '../../services/characterService';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import EmojiPicker from 'emoji-picker-react';

const CreatePost = () => {
  const { firestoreUser } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('outOfCharacter');
  const [characterId, setCharacterId] = useState('');
  const [userCharacters, setUserCharacters] = useState([]);  const [loadingCharacters, setLoadingCharacters] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [emojiPickerAnchor, setEmojiPickerAnchor] = useState(null);
  const titleFieldRef = useRef(null);

  const isDM = firestoreUser && firestoreUser.roles && firestoreUser.roles.dungeonMaster;

  /**
   * Fetches user's characters when creating in-character posts
   */
  useEffect(() => {
    const fetchUserCharacters = async () => {
      if (firestoreUser && firestoreUser.uid && postType === 'inCharacter') {
        setLoadingCharacters(true);
        try {
          const chars = await getCharactersByUserId(firestoreUser.uid);
          setUserCharacters(chars);
          if (chars.length === 1) {
            setCharacterId(chars[0].id);
          }
        } catch (err) {
          setError('Failed to fetch characters: ' + err.message);
          setUserCharacters([]);
        }
        setLoadingCharacters(false);
      }
    };

    fetchUserCharacters();
  }, [firestoreUser, postType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!title.trim() || !content.trim()) {
      setError('Title and content cannot be empty.');
      return;
    }    if (postType === 'inCharacter' && !characterId) {
      setError('Please select a character for an in-character post.');
      return;
    }

    const isDM = firestoreUser && firestoreUser.roles && firestoreUser.roles.dungeonMaster;
    if (postType === 'dungeonMaster' && !isDM) {
      setError('You do not have permission to create Dungeon Master posts.');
      return;
    }

    setSubmitting(true);
    try {
      let characterName = null;
      let characterImageUrl = null;

      if (postType === 'inCharacter' && characterId) {
        const selectedCharacter = userCharacters.find(char => char.id === characterId);
        if (selectedCharacter) {
          characterName = selectedCharacter.name;
          characterImageUrl = selectedCharacter.imageUrl || null;
        }
      }

      const postData = {
        userId: firestoreUser.uid,
        userName: firestoreUser.name || "Anonymous",
        title,
        content,
        postType,
        characterId: postType === 'inCharacter' ? characterId : null,
        characterName: postType === 'inCharacter' ? characterName : null,
        characterImageUrl: postType === 'inCharacter' ? characterImageUrl : null,
      };
      const postId = await createPost(postData);
      setSuccess(`Post created successfully! Post ID: ${postId}`);
      setTitle('');
      setContent('');
      setPostType('outOfCharacter');
      setCharacterId('');
      navigate('/town-square');
    } catch (err) {
      console.error("Failed to create post:", err);
      setError(err.message || "Failed to create post. Please try again.");
    }
    setSubmitting(false);
  };
  const handleEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    const titleField = titleFieldRef.current?.querySelector('input');
    
    if (titleField) {
      const start = titleField.selectionStart;
      const end = titleField.selectionEnd;
      const newText = title.slice(0, start) + emoji + title.slice(end);
      setTitle(newText);
      
      setEmojiPickerAnchor(null);
      
      setTimeout(() => {
        titleField.focus();
        titleField.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 100);
    } else {
      setTitle(prev => prev + emoji);
      setEmojiPickerAnchor(null);
    }
  };

  const handleEmojiButtonClick = (event) => {
    setEmojiPickerAnchor(event.currentTarget);
  };

  const handleEmojiPickerClose = () => {
    setEmojiPickerAnchor(null);
  };

  return (
    <Paper elevation={3} sx={{ m: 'auto', mt: 4, p: 3, maxWidth: 700, bgcolor: '#333', color: '#fff' }}>      <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#d98d2a', fontFamily: '"Georgia", serif' }}>
        Post a New Notice to the Conference Clamp
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Box sx={{ position: 'relative' }}>
          <TextField
            ref={titleFieldRef}
            label="Post Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            margin="normal"
            placeholder="Enter an engaging title... 😊"
            InputLabelProps={{ style: { color: '#d98d2a' } }}
            inputProps={{ style: { color: '#F5F5F5' } }}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d98d2a' } } }}
          />
          
          {/* Emoji Picker Button for Title */}
          <IconButton
            onClick={handleEmojiButtonClick}
            sx={{
              position: 'absolute',
              top: 20,
              right: 8,
              color: '#d98d2a',
              '&:hover': {
                backgroundColor: 'rgba(217, 141, 42, 0.1)',
              }
            }}
            aria-label="add emoji to title"
          >
            <EmojiEmotionsIcon />
          </IconButton>
          
          {/* Emoji Picker Popover */}
          <Popover
            open={Boolean(emojiPickerAnchor)}
            anchorEl={emojiPickerAnchor}
            onClose={handleEmojiPickerClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              '& .MuiPopover-paper': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
              }
            }}
          >
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              theme="dark"
              emojiStyle="native"
              width={300}
              height={400}
            />
          </Popover>
        </Box>

        <FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
          <InputLabel id="post-type-label" sx={{ color: '#d98d2a' }}>Post Type</InputLabel>
          <Select
            labelId="post-type-label"
            value={postType}
            label="Post Type"
            onChange={(e) => {
              setPostType(e.target.value);
              if (e.target.value !== 'inCharacter') {
                setCharacterId('');
              }
            }}
            sx={{ color: '#F5F5F5', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' }, '& .MuiSvgIcon-root': { color: '#d98d2a' } }}
          >
            <MenuItem value="outOfCharacter">Out of Character</MenuItem>
            <MenuItem value="inCharacter">In Character</MenuItem>
            {isDM && <MenuItem value="dungeonMaster">Dungeon Master</MenuItem>}
          </Select>
        </FormControl>

        {postType === 'inCharacter' && (
          <FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
            <InputLabel id="character-select-label" sx={{ color: '#d98d2a' }}>Posting as Character</InputLabel>
            {loadingCharacters ? (
              <CircularProgress sx={{color: '#d98d2a'}} />
            ) : userCharacters.length > 0 ? (
              <Select
                labelId="character-select-label"
                value={characterId}
                label="Posting as Character"
                onChange={(e) => setCharacterId(e.target.value)}
                required
                sx={{ color: '#F5F5F5', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' }, '& .MuiSvgIcon-root': { color: '#d98d2a' } }}
              >
                {userCharacters.map((char) => (
                  <MenuItem key={char.id} value={char.id}>{char.name} (Lvl {char.level} {char.race} {char.class})</MenuItem>
                ))}
              </Select>
            ) : (
              <Typography sx={{color: '#aaa', mt:1}}>No characters found for your account. You cannot post 'In Character'.</Typography>
            )}
          </FormControl>
        )}        <Box sx={{
          my: 2,
          '& .ql-toolbar.ql-snow': {
            borderColor: '#d98d2a !important',
            borderRadius: '4px 4px 0 0',
            backgroundColor: '#383838',
            padding: '8px',

            '& button .ql-stroke, & .ql-picker-label .ql-stroke': { stroke: '#F5F5F5 !important' },
            '& button .ql-fill, & .ql-picker-label .ql-fill': { fill: '#F5F5F5 !important' },
            '& button .ql-even .ql-stroke, & .ql-picker-label .ql-even .ql-stroke': { stroke: '#F5F5F5 !important' },

            '& .ql-picker-label': {
              color: '#F5F5F5 !important',
            },
            '& .ql-picker-label:hover': {
              color: '#d98d2a !important',
              '& .ql-stroke': { stroke: '#d98d2a !important' },
              '& .ql-fill': { fill: '#d98d2a !important' },
            },

            '& button:hover': {
              backgroundColor: '#4a4a4a !important',
              '& .ql-stroke': { stroke: '#d98d2a !important' },
              '& .ql-fill': { fill: '#d98d2a !important' },            },
            '& button.ql-active': {
              backgroundColor: '#555 !important',
              '& .ql-stroke': { stroke: '#d98d2a !important' },
              '& .ql-fill': { fill: '#d98d2a !important' },
            },

            '& .ql-picker-options': {
              backgroundColor: '#4a4a4a !important',
              borderColor: '#d98d2a !important',
              borderRadius: '2px',
              boxShadow: '0px 2px 5px rgba(0,0,0,0.2)',
              '& .ql-picker-item': {
                color: '#F5F5F5 !important',
              },
              '& .ql-picker-item:hover': {
                backgroundColor: '#5a5a5a !important',
                color: '#d98d2a !important',
              },
              '& .ql-picker-item.ql-selected': {
                color: '#d98d2a !important',
              },
            },
            '& .ql-color-picker .ql-picker-item:hover, & .ql-background-picker .ql-picker-item:hover': {
                outline: '1px solid #d98d2a !important',
                outlineOffset: '-1px',
            }
          },

          '& .ql-container.ql-snow': {
            borderColor: '#d98d2a !important',
            color: '#F5F5F5',
            backgroundColor: '#2a2a2a',
            borderRadius: '0 0 4px 4px',
          },
          '& .ql-editor': {
            minHeight: '250px',
            color: '#F5F5F5',
            backgroundColor: '#2a2a2a',
            '&.ql-blank::before': {
              color: '#F5F5F5',
              fontStyle: 'italic',
            },
            caretColor: '#F5F5F5',          },

          '& .ql-tooltip': {
            backgroundColor: '#383838',
            borderColor: '#d98d2a',
            color: '#F5F5F5',
            boxShadow: '0px 0px 10px rgba(0,0,0,0.5) !important',
            '& input[type="text"]': {
              backgroundColor: '#4a4a4a',
              color: '#F5F5F5',
              borderColor: '#d98d2a',
              borderRadius: '2px',
            },
            '& a.ql-action::before': {
              backgroundColor: '#d98d2a !important',
              color: '#1c1c1c !important',
              padding: '3px 5px',
              borderRadius: '2px',
              marginRight: '5px',
            },
            '& a.ql-remove::before': {
              color: '#F5F5F5 !important',
              padding: '3px 5px',
            }
          }
        }}>
          <InputLabel shrink sx={{ color: '#d98d2a', mb: 1, position: 'static', transform: 'none' }}>Content</InputLabel>
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            placeholder="Share your thoughts, decrees, or tales..."
            modules={{
              toolbar: {
                container: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                  [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                  ['link'],
                  [{ 'color': [] }, { 'background': [] }],
                  [{ 'font': [] }],
                  [{ 'align': [] }],
                  ['clean']
                ],              },
            }}
          />
        </Box>

        <Button 
          type="submit" 
          variant="contained" 
          disabled={submitting || (postType === 'inCharacter' && loadingCharacters)}
          sx={{ 
            fontFamily: 'inherit',
            backgroundColor: '#d98d2a', 
            color: '#1c1c1c',
            '&:hover': { backgroundColor: '#c87d26' },
            mr: 1
          }}
        >
          {submitting ? <CircularProgress size={24} sx={{color: '#1c1c1c'}} /> : "Create Post"}
        </Button>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ color: '#d98d2a', borderColor: '#d98d2a' }}>
          Cancel
        </Button>
      </Box>
    </Paper>
  );
};

export default CreatePost;
